// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.
import "../../../style/sassStyle";

import { DummyEvent, QuestionnaireInputProps } from "./QuestionnaireTypes";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import React, { useCallback, useState } from "react";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { getHours, parse as parseDate, setHours } from "date-fns";

import DateFnsUtils from "@date-io/date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconName } from "@fortawesome/fontawesome-svg-core";
import { InputAdornment } from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    datePicker: {
      width: "100%",
    },
  })
);

const dateFormat = "MM/dd/yyyy";

/**
 * Renders a date input in a questionaire.
 * You should not need to use this outside of the Questionnaire component.
 */
const QuestionnaireInputDate = (props: QuestionnaireInputProps) => {
  const { question, onChange } = props;
  const [selectedDate, setSelectedDate] = useState(
    question.value
      ? new Date(question.value)
      : question.placeholder
      ? new Date(question.placeholder)
      : null
  );
  const classes = useStyles();

  if (question.type !== "date") {
    throw Error(
      `Invalid question type ${question.type} for QuestionnaireInputDate.`
    );
  }

  const onDateChanged = useCallback(
    (date: MaterialUiPickersDate, value?: string | null | undefined) => {
      if (!date || isNaN(date.getTime()) || !value) {
        const event: DummyEvent = {
          currentTarget: {
            name: question.name,
            value: value || null,
          },
        };
        onChange(event);
        // date invalid
        return;
      }
      const currentTime = new Date();
      let parsedValue = parseDate(value, dateFormat, new Date());
      parsedValue = setHours(parsedValue, getHours(currentTime));

      setSelectedDate(parsedValue);
      const event: DummyEvent = {
        currentTarget: {
          name: question.name,
          value: parsedValue.toISOString(),
        },
      };
      onChange(event);
    },
    [onChange, question.name]
  );

  let label = question.label;
  if (label && question.mandatory) {
    label += " *";
  }
  const otherProps: any = {};
  if (question.maxDate) {
    otherProps.maxDate = question.maxDate;
  }
  if (question.minDate) {
    otherProps.minDate = question.minDate;
  }
  if (question.disableFuture) {
    otherProps.disableFuture = question.disableFuture;
  }
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <KeyboardDatePicker
        color="primary"
        className={classes.datePicker}
        error={!!question.error}
        format={dateFormat}
        helperText={question.error || " "}
        InputProps={{
          startAdornment: (
            <>
              {question.icon && (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={question.icon as IconName} />
                </InputAdornment>
              )}
            </>
          ),
        }}
        inputVariant="standard"
        KeyboardButtonProps={{
          "aria-label": "change date",
        }}
        label={label}
        margin="none"
        onChange={onDateChanged}
        value={selectedDate || null}
        variant="dialog"
        {...otherProps}
      />
    </MuiPickersUtilsProvider>
  );
};

export default QuestionnaireInputDate;
